
import { defineComponent, ref, computed, onMounted } from "vue";
import { fabric } from "fabric";
import { store } from "@/store";

import ToolTitle from "@/components/SideToolBar/Tools/ToolTitle.vue";

import {
  getSheetScale,
  checkBleed,
  setLabelGroupAngle,
  setClipPath,
  addGroups,
} from "@/canvas-api/PreviewCanvas";

import useMountCanvas from "@/composables/useMountCanvas";
import usePrintPreview from "@/composables/usePrintPreview";

import { SheetLabel } from "@/types";

export default defineComponent({
  name: "Layout",
  components: {
    ToolTitle,
  },
  setup() {
    const maxWidth = 315;
    const title = ref("");
    const layoutCanvasRef = ref<HTMLCanvasElement>();
    const layoutCanvas = ref<fabric.Canvas>({} as fabric.Canvas);
    const canvasWidth = ref(0);
    const canvasHeight = ref(0);

    const dpi = computed<number>(() => store.getters.dpi);
    const currentLabel = computed<SheetLabel>(() => store.getters.currentLabel);
    const isRotated = computed<boolean>(() =>
      store.getters.canvasOrientation === "rotated" ? true : false
    );

    onMounted(() => {
      //Trigger rerender
      title.value = "Layout";
      setupLayout();
    });

    const setCanvasDimensions = (width: number) => {
      canvasWidth.value = width;
      canvasHeight.value =
        (currentLabel.value.sheet_size_height / currentLabel.value.sheet_size_width) * width;
    };

    const setupLayout = async () => {
      const labelGroup = await usePrintPreview();

      try {
        if (labelGroup) {
          setCanvasDimensions(maxWidth);

          layoutCanvas.value = useMountCanvas(
            layoutCanvas.value,
            layoutCanvasRef.value,
            canvasWidth.value,
            canvasHeight.value,
            "#fff"
          );
          layoutCanvas.value.selection = false;

          setLabelGroupAngle(labelGroup, isRotated.value);
          const label = checkBleed(labelGroup, currentLabel.value.bleed);

          if (label) {
            label.set({
              strokeWidth: 0,
            });

            const sheetScale = getSheetScale(
              canvasWidth.value,
              currentLabel.value.sheet_size_width,
              dpi.value
            );
            const { x: labelLeft, y: labelTop } = label.getCenterPoint();

            if (isRotated.value) {
              const left = (-labelTop + label.getScaledWidth() / 2) * sheetScale;
              const top = (labelLeft + label.getScaledHeight() / 2) * sheetScale;

              labelGroup.set({
                left,
                top,
                scaleX: sheetScale,
                scaleY: sheetScale,
              });
            } else {
              const left = (-labelLeft + label.getScaledWidth() / 2) * sheetScale;
              const top = (-labelTop + label.getScaledHeight() / 2) * sheetScale;

              labelGroup.set({
                left,
                top,
                scaleX: sheetScale,
                scaleY: sheetScale,
              });
            }

            await setClipPath(
              labelGroup,
              label,
              currentLabel.value.shape_id,
              currentLabel.value.mask_id, 
              currentLabel.value.mask_url
            );

            addGroups(
              currentLabel.value,
              labelGroup,
              label,
              sheetScale,
              layoutCanvas.value,
              canvasWidth.value,
              canvasHeight.value
            );
          } else {
            throw new Error("Missing labelGroup, Layout.vue");
          }
        }
      } catch (e) {
        console.log(e);
      }
    };

    return {
      title,
      layoutCanvasRef,
      layoutCanvas,
      canvasWidth,
      canvasHeight,
    };
  },
});
